import api from './api'
import { toCamelCase } from '@/utils/transform'

/**
 * The command center needs report history as well as schedules.  Keeping the
 * mapping here prevents the reporting screen from depending on the API's
 * snake_case representation.
 */
export type ReportHistoryItem = {
  id: string
  template: string
  title: string
  loanId: string | null
  status: string
  format: string
  sizeBytes: number
  generatedBy: string | null
  createdAt: string | null
  /** The artifact itself is still in storage, as against a row saying one
   *  was produced (the server decides; see GET /reports/history). */
  downloadable?: boolean
  /** The schedule this file belongs to, when the scheduler or a schedule's Run Now made it. */
  scheduledReportId?: string | null
  /** Written only by a scheduled (or event) fire. */
  scheduleRunId?: string | null
  /** The saved report this file is a run of, when it was made from one (and the server recorded it). */
  definitionId?: string | null
  /** scheduled = the scheduler made it; manual = a person asked. Absent from an older server. */
  trigger?: 'scheduled' | 'manual'
  error?: string | null
}

/** The server's filename for a download, when it sent one. */
function dispositionFilename(disposition: unknown): string | null {
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/.exec(String(disposition ?? ''))
  return match?.[1] ?? null
}

export const reportingCenterService = {
  /** Generate a history row's report again with the template, filters and
   *  format it was first generated with (the server holds them). */
  rerunHistory: (reportId: string) =>
    api.post(`/reports/history/${encodeURIComponent(reportId)}/rerun`, {}, { responseType: 'blob' }),
  /** Fetch a stored report's original bytes. This goes through the API
   *  client so the request carries the signed-in user's bearer token — a
   *  plain link to the API is a browser navigation, which carries none and
   *  is refused (401) wherever real authentication is on. */
  downloadHistory: async (
    reportId: string,
    fallbackFilename: string,
  ): Promise<{ blob: Blob; filename: string }> => {
    const response = await api.get(`/reports/history/${encodeURIComponent(reportId)}/download`, {
      responseType: 'blob',
    })
    return {
      blob: response.data as Blob,
      filename: dispositionFilename(response.headers?.['content-disposition']) ?? fallbackFilename,
    }
  },
  listHistory: (limit = 200) =>
    api
      .get('/reports/history', { params: { limit } })
      .then((response) => toCamelCase<ReportHistoryItem[]>(response.data)),
}
